package dev.kuml.portal.web.components

import dev.kuml.portal.web.api.AvatarUrl
import io.kvision.core.Container
import io.kvision.html.Div
import io.kvision.html.div
import io.kvision.html.image
import io.kvision.html.span
import org.w3c.dom.HTMLImageElement

/** Pure, DOM-free initials derivation for the fallback shown before/instead of an avatar image
 * (see `AvatarInitialsTest`). Mirrors common "two-letter avatar fallback" conventions: first
 * letter of the first two whitespace-separated words, uppercased; `"?"` for a blank/empty name. */
public object AvatarInitials {
    public fun of(displayName: String): String {
        val words = displayName.trim().split(Regex("\\s+")).filter { it.isNotEmpty() }
        if (words.isEmpty()) return "?"
        val first = words[0].take(1)
        val second = if (words.size > 1) words[1].take(1) else ""
        return (first + second).uppercase()
    }
}

/** @property element the avatar's root container. @property refresh re-renders the widget against
 * a new cache-buster (e.g. right after a successful own-avatar upload) -- see
 * `dev.kuml.portal.web.api.AvatarUrl`'s KDoc for why the cache-buster matters. */
public class AvatarWidget(
    public val element: Div,
    public val refresh: (String?) -> Unit,
)

/**
 * Round avatar with an always-present initials fallback (see [AvatarInitials]) underneath an
 * `<img>` pointed at [dev.kuml.portal.web.api.AvatarUrl]. The `<img>` is hidden via CSS on load
 * error (no avatar set / 404 / network hiccup), leaving the initials visible with no "broken
 * image" icon and no flash of empty space while it loads.
 *
 * Deliberately TWO SIBLING widget calls (`span()` then `image()`) inside the same `div{}`, and no
 * mixing of `content =`/`content +=` with child-widget calls in that container -- see this vault's
 * CLAUDE.md / the kUML-Portal repo conventions on the `div{}`-child-widget rendering-order bug this
 * avoids. No `innerHTML` anywhere.
 *
 * @param title R-28: optional `title`/`aria-label` on the container -- `null` (the default) leaves
 *   both attributes unset, unchanged from every pre-R-28 call site. `EditorScreen.kt`'s collab
 *   avatar strip passes [CollabPresenceFormat.avatarTitle] here so a peer's presence tooltip shows
 *   what they currently have selected, if anything.
 */
public fun Container.avatarWidget(
    userId: String,
    displayName: String,
    cacheBuster: String?,
    sizeClass: String,
    title: String? = null,
): AvatarWidget {
    val container = div(className = "kportal-avatar $sizeClass")
    if (title != null) {
        container.setAttribute("title", title)
        container.setAttribute("aria-label", title)
    }

    fun render(buster: String?) {
        container.removeAll()
        container.span(AvatarInitials.of(displayName), className = "kportal-avatar-fallback")
        val img = container.image(src = AvatarUrl.forUser(userId, buster), alt = "")
        img.addCssClass("kportal-avatar-img")
        img.addAfterInsertHook {
            val element = img.getElement() as? HTMLImageElement ?: return@addAfterInsertHook
            element.addEventListener("error", { img.addCssClass("kportal-hidden") })
        }
    }

    render(cacheBuster)
    return AvatarWidget(container) { newBuster -> render(newBuster) }
}
